/**
 * Layer B — declarative-attribute tracking for interactions the native `@shopify/events`
 * bus doesn't cover (nav/content clicks, section engagement). Unlike Layer A, this layer
 * pushes to window.dataLayer (for GTM to read) in addition to Shopify.analytics.publish,
 * because these events aren't reachable through the Google & YouTube channel app.
 *
 * Authoring convention — add to any element:
 *   data-analytics-event="select_navigation"
 *   data-analytics-content-type="global_nav"
 *   data-analytics-content-id="shop"
 * Every `data-analytics-*` attribute except `data-analytics-event` becomes a param
 * (kebab-case in HTML -> snake_case param name: `data-analytics-content-type` ->
 * `content_type`). `<a href>` elements automatically get `link_url`/`link_domain`.
 *
 * For section-view tracking (fires once per element per page, 50% visible), add:
 *   data-analytics-section-name="hero"
 * to the section's wrapper — no event name needed, it always fires `section_view`.
 *
 * See project-context/log/tracking/tracking-plan-code-spec-v1.md for the full file-by-file attribute
 * checklist (P0 wired directly in markup already; P1 still to be added).
 */
import { commonParams, logEvent, publishToShopify, isDesignMode } from '@theme/analytics-common';

function paramsFromDataset(el) {
  const params = {};
  for (const [key, value] of Object.entries(el.dataset)) {
    if (key === 'analyticsEvent' || !key.startsWith('analytics')) continue;
    const rest = key.slice('analytics'.length);
    if (!rest) continue;
    const paramName = (rest[0].toLowerCase() + rest.slice(1)).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    params[paramName] = value;
  }
  return params;
}

/**
 * Public dispatch for the rare cases that can't be expressed as a clicked/changed
 * element with data-analytics-* attributes — e.g. newsletter-popup.js's post-redirect
 * success/error detection, which fires on page load, not on a click.
 */
export function dispatch(eventName, params = {}) {
  dispatchToGtm(eventName, params);
}

function dispatchToGtm(eventName, params = {}) {
  if (isDesignMode()) return;

  const payload = { ...commonParams(), ...params };
  logEvent('B', eventName, payload);

  window.dataLayer = window.dataLayer || [];
  // Clear any stale ecommerce object from a prior push before a non-ecommerce custom
  // event, so GA4/GTM tags reading `ecommerce` don't pick up leftover data from an
  // earlier, unrelated event on the same page.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event: eventName, ...payload });

  publishToShopify(eventName, payload);
}

function handleInteraction(event) {
  const el = event.target.closest('[data-analytics-event]');
  if (!el) return;

  const eventName = el.dataset.analyticsEvent;
  const params = paramsFromDataset(el);

  if (el.tagName === 'A' && el.href) {
    if (!('linkUrl' in params)) params.link_url = el.href;
    if (!('linkDomain' in params)) {
      try {
        params.link_domain = new URL(el.href, window.location.href).hostname;
      } catch {
        /* relative/invalid URL — skip link_domain rather than report a wrong one */
      }
    }
  }

  if (el.tagName === 'SELECT' && !('value' in params)) {
    // A data-analytics-value on the selected <option> always wins (explicit author intent).
    // Otherwise use the option's own value if it's a meaningful, non-empty token (e.g. an ISO
    // country code) — falling back to the option's visible text only when the functional
    // `value` is empty/non-descriptive (e.g. "" meaning "stay on this site").
    const selected = el.options[el.selectedIndex];
    params.value = selected?.dataset.analyticsValue ?? (el.value || selected?.text) ?? '';
  } else if (el.tagName === 'INPUT' && !('value' in params)) {
    params.value = el.value;
  }

  dispatchToGtm(eventName, params);
}

document.addEventListener('click', handleInteraction);
document.addEventListener('change', handleInteraction);

// --- Section-view: fires `section_view` once per [data-analytics-section-name]
// element per page load, at 50% visibility. ---
const seenSections = new WeakSet();
const sectionObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || seenSections.has(entry.target)) return;
      seenSections.add(entry.target);
      sectionObserver.unobserve(entry.target);
      dispatchToGtm('section_view', paramsFromDataset(entry.target));
    });
  },
  { threshold: 0.5 }
);

function observeSections(root) {
  root.querySelectorAll('[data-analytics-section-name]').forEach((el) => {
    if (seenSections.has(el)) return;
    sectionObserver.observe(el);
  });
}

function initSectionObserver() {
  observeSections(document);
  // Re-scan on Shopify section re-renders (theme editor, dynamic sections API) so
  // lazily-hydrated/re-rendered sections still get observed.
  document.addEventListener('shopify:section:load', () => observeSections(document));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSectionObserver, { once: true });
} else {
  initSectionObserver();
}
